import React, { useRef } from 'react'
import { Table, Input, Select, Button } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import type { FlowStepInput } from '@/data'

interface ButtonsListEditorProps {
  buttons: FlowStepInput[]
  onChange: (buttons: FlowStepInput[]) => void
  showValue?: boolean
}

export const ButtonsListEditor: React.FC<ButtonsListEditorProps> = ({
  buttons,
  onChange,
  showValue = false,
}) => {
  const idCounter = useRef(0)

  const updateButton = (
    index: number,
    field: 'label' | 'value' | 'action',
    value: string,
  ) => {
    const updated = buttons.map((btn, i) =>
      i === index ? { ...btn, [field]: value } : btn,
    )
    onChange(updated)
  }

  const addButton = () => {
    const newId = `btn_${++idCounter.current}`
    onChange([
      ...buttons,
      {
        id: newId,
        label: 'New Option',
        type: 'radio' as const,
        placeholder: null,
        required: false,
        action: 'progress' as const,
      },
    ])
  }

  const removeButton = (index: number) => {
    onChange(buttons.filter((_, i) => i !== index))
  }

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      render: (val: string, _: FlowStepInput, index: number) => (
        <Input
          size="small"
          value={val}
          onChange={(e) => updateButton(index, 'label', e.target.value)}
        />
      ),
    },
    ...(showValue
      ? [
          {
            title: 'Value',
            dataIndex: 'value',
            width: 120,
            render: (val: string, _: FlowStepInput, index: number) => (
              <Input
                size="small"
                value={val}
                onChange={(e) => updateButton(index, 'value', e.target.value)}
              />
            ),
          },
        ]
      : []),
    {
      title: 'Action',
      dataIndex: 'action',
      width: 120,
      render: (val: string, _: FlowStepInput, index: number) => (
        <Select
          size="small"
          value={val}
          style={{ width: '100%' }}
          onChange={(v) => updateButton(index, 'action', v)}
          options={[
            { value: 'progress', label: 'Progress' },
            { value: 'downsell', label: 'Downsell' },
          ]}
        />
      ),
    },
    {
      title: '',
      width: 40,
      render: (_: unknown, __: FlowStepInput, index: number) =>
        buttons.length > 1 ? (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeButton(index)}
          />
        ) : null,
    },
  ]

  return (
    <div>
      <Table
        dataSource={buttons}
        columns={columns}
        rowKey={(_, index) => String(index)}
        pagination={false}
        size="small"
      />
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addButton}
        style={{ width: '100%', marginTop: 8 }}
      >
        Add Button
      </Button>
    </div>
  )
}
